import React, { useEffect, useState } from 'react';
import { createPortal } from 'react-dom';
import {
  PlusIcon,
  PencilIcon,
  TrashIcon,
  XMarkIcon,
  CheckIcon
} from '@heroicons/react/24/outline';
import {
  useReactTable,
  getCoreRowModel,
  getSortedRowModel,
  getFilteredRowModel,
  getPaginationRowModel,
  flexRender,
  createColumnHelper,
  SortingState,
  ColumnFiltersState
} from '@tanstack/react-table';
import {
  TemplateTasksClient,
  TemplateTaskListDto,
  CreateTemplateTaskCommand,
  UpdateTemplateTaskCommand,
  TemplateCategoriesClient,
  TemplateCategoryDto
} from '../../web-api-client';
import { API_CONFIG } from '../../config/api';
import { toast } from 'sonner';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../../components/ui/select';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { usersService, User } from '../../services/usersService';
import { teamsService } from '../../services/teamsService';
import { TeamDto } from '../../web-api-client';
import { getErrorMessage } from '../../utils/errorHelpers';

const columnHelper = createColumnHelper<TemplateTaskListDto>();

// Modal component - extracted outside to prevent re-creation on every render
interface ModalProps {
  show: boolean;
  onClose: () => void;
  title: string;
  children: React.ReactNode;
}

const Modal: React.FC<ModalProps> = ({ show, onClose, title, children }) => {
  useEffect(() => {
    if (show) {
      // Disable body scroll when modal is open
      document.body.style.overflow = 'hidden';
    } else {
      // Re-enable body scroll when modal is closed
      document.body.style.overflow = 'unset';
    }

    // Cleanup function to ensure scroll is re-enabled
    return () => {
      document.body.style.overflow = 'unset';
    };
  }, [show]);

  if (!show) return null;

  const handleBackdropClick = (e: React.MouseEvent) => {
    // Only close if clicking directly on the backdrop, not on children
    if (e.target === e.currentTarget) {
      onClose();
    }
  };

  return createPortal(
    <div
      className="fixed inset-0 z-50 overflow-y-auto"
      onClick={handleBackdropClick}
    >
      <div className="flex min-h-screen items-center justify-center p-4">
        <div className="fixed inset-0 bg-black bg-opacity-50 transition-opacity" aria-hidden="true" />
        <div
          className="relative bg-white dark:bg-gray-800 rounded-lg shadow-xl max-w-2xl w-full"
          onClick={(e) => e.stopPropagation()}
        >
          <div className="flex items-center justify-between p-6 border-b border-gray-200 dark:border-gray-700">
            <h3 className="text-lg font-semibold text-gray-900 dark:text-white">{title}</h3>
            <button
              type="button"
              onClick={onClose}
              className="text-gray-400 hover:text-gray-600 transition-colors"
            >
              <XMarkIcon className="h-6 w-6" />
            </button>
          </div>
          <div className="p-6">
            {children}
          </div>
        </div>
      </div>
    </div>,
    document.body
  );
};

export default function TaskTemplates() {
  const [tasks, setTasks] = useState<TemplateTaskListDto[]>([]);
  const [loading, setLoading] = useState(true);
  const [showAddModal, setShowAddModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);
  const [selectedTask, setSelectedTask] = useState<TemplateTaskListDto | null>(null);
  const [formData, setFormData] = useState({
    taskName: '',
    description: '',
    requiresApproval: false,
    approverEmail: '',
    defaultPicId: '',
    defaultTeamId: undefined as number | undefined,
    templateCategoryId: undefined as number | undefined
  });
  const [submitting, setSubmitting] = useState(false);

  // Categories for task organization
  const [categories, setCategories] = useState<TemplateCategoryDto[]>([]);

  // Users and teams for assignment
  const [users, setUsers] = useState<User[]>([]);
  const [teams, setTeams] = useState<TeamDto[]>([]);

  // TanStack Table state
  const [sorting, setSorting] = useState<SortingState>([]);
  const [columnFilters, setColumnFilters] = useState<ColumnFiltersState>([]);
  const [globalFilter, setGlobalFilter] = useState('');

  // Define columns
  const columns = [
    columnHelper.accessor('taskName', {
      header: 'Task Name',
      cell: info => (
        <div className="text-sm font-semibold text-gray-900 dark:text-white">
          {info.getValue()}
        </div>
      ),
    }),
    columnHelper.accessor('templateCategoryName', {
      header: 'Category',
      cell: info => (
        <span className="px-2 py-1 text-xs font-medium bg-gray-100 dark:bg-gray-700 text-gray-800 dark:text-gray-200 rounded-full">
          {info.getValue() || 'Uncategorized'}
        </span>
      ),
    }),
    columnHelper.accessor('requiresApproval', {
      header: 'Approval',
      cell: info => (
        info.getValue() ? (
          <CheckIcon className="h-5 w-5 text-green-600" />
        ) : (
          <span className="text-gray-400">-</span>
        )
      ),
    }),
    columnHelper.display({
      id: 'actions',
      header: () => <div className="text-right">Actions</div>,
      cell: info => (
        <div className="flex items-center justify-end space-x-2">
          <button
            onClick={() => handleEdit(info.row.original)}
            className="inline-flex items-center p-2 text-blue-600 hover:text-blue-900 hover:bg-blue-50 rounded-lg transition-colors"
            title="Edit task"
          >
            <PencilIcon className="h-5 w-5" />
          </button>
          <button
            onClick={() => handleDeleteClick(info.row.original)}
            className="inline-flex items-center p-2 text-red-600 hover:text-red-900 hover:bg-red-50 rounded-lg transition-colors"
            title="Delete task"
          >
            <TrashIcon className="h-5 w-5" />
          </button>
        </div>
      ),
    }),
  ];

  const table = useReactTable({
    data: tasks,
    columns,
    state: {
      sorting,
      columnFilters,
      globalFilter,
    },
    onSortingChange: setSorting,
    onColumnFiltersChange: setColumnFilters,
    onGlobalFilterChange: setGlobalFilter,
    getCoreRowModel: getCoreRowModel(),
    getSortedRowModel: getSortedRowModel(),
    getFilteredRowModel: getFilteredRowModel(),
    getPaginationRowModel: getPaginationRowModel(),
    initialState: {
      pagination: {
        pageSize: 10,
      },
    },
  });

  useEffect(() => {
    fetchTasks();
    fetchCategories();
    fetchUsers();
    fetchTeams();
  }, []);

  const fetchTasks = async () => {
    try {
      setLoading(true);
      const client = new TemplateTasksClient(API_CONFIG.baseUrl);
      const data = await client.getTemplateTasks(undefined, undefined, true);
      setTasks(data);
    } catch (error) {
      console.error('Failed to fetch template tasks:', error);
      toast.error('Failed to load template tasks');
    } finally {
      setLoading(false);
    }
  };

  const fetchCategories = async () => {
    try {
      const client = new TemplateCategoriesClient(API_CONFIG.baseUrl);
      const data = await client.getTemplateCategories(true);
      setCategories(data);
    } catch (error) {
      console.error('Failed to fetch categories:', error);
    }
  };

  const fetchUsers = async () => {
    try {
      const usersData = await usersService.getUsers();
      setUsers(usersData);
    } catch (error) {
      console.error('Failed to fetch users:', error);
    }
  };

  const fetchTeams = async () => {
    try {
      const teamsData = await teamsService.getTeams();
      setTeams(teamsData);
    } catch (error) {
      console.error('Failed to fetch teams:', error);
    }
  };

  const handleEdit = async (task: TemplateTaskListDto) => {
    setSelectedTask(task);
    setFormData({
      taskName: task.taskName || '',
      description: task.description || '',
      requiresApproval: task.requiresApproval || false,
      approverEmail: task.approverEmail || '',
      defaultPicId: task.defaultPicId || '',
      defaultTeamId: task.defaultTeamId || undefined,
      templateCategoryId: task.templateCategoryId || undefined
    });
    setShowEditModal(true);
  };

  const handleDeleteClick = (task: TemplateTaskListDto) => {
    setSelectedTask(task);
    setShowDeleteDialog(true);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    // Validation
    if (!formData.taskName.trim()) {
      toast.error('Task name is required');
      return;
    }

    if (formData.requiresApproval && !formData.approverEmail?.trim()) {
      toast.error('Approver email is required when approval is needed');
      return;
    }

    try {
      setSubmitting(true);
      const client = new TemplateTasksClient(API_CONFIG.baseUrl);

      if (showEditModal && selectedTask) {
        // Update
        const command = new UpdateTemplateTaskCommand({
          id: selectedTask.id!,
          taskName: formData.taskName,
          description: formData.description || undefined,
          requiresApproval: formData.requiresApproval,
          approverEmail: formData.approverEmail || undefined,
          defaultPicId: formData.defaultPicId || undefined,
          defaultTeamId: formData.defaultTeamId,
          templateCategoryId: formData.templateCategoryId,
          isActive: true
        });
        await client.updateTemplateTask(selectedTask.id!, command);
        toast.success('Task template updated successfully');
      } else {
        // Create
        const command = new CreateTemplateTaskCommand({
          taskName: formData.taskName,
          description: formData.description || undefined,
          requiresApproval: formData.requiresApproval,
          approverEmail: formData.approverEmail || undefined,
          defaultPicId: formData.defaultPicId || undefined,
          defaultTeamId: formData.defaultTeamId,
          templateCategoryId: formData.templateCategoryId
        });
        await client.createTemplateTask(command);
        toast.success('Task template created successfully');
      }

      setShowAddModal(false);
      setShowEditModal(false);
      resetForm();
      fetchTasks();
    } catch (error: unknown) {
      console.error('Failed to save task template:', error);
      toast.error(getErrorMessage(error, 'Failed to save task template'));
    } finally {
      setSubmitting(false);
    }
  };

  const handleDelete = async () => {
    if (!selectedTask) return;

    try {
      setSubmitting(true);
      const client = new TemplateTasksClient(API_CONFIG.baseUrl);
      await client.deleteTemplateTask(selectedTask.id!);
      toast.success('Task template deleted successfully');
      setShowDeleteDialog(false);
      setSelectedTask(null);
      fetchTasks();
    } catch (error: unknown) {
      console.error('Failed to delete task template:', error);
      toast.error(getErrorMessage(error, 'Failed to delete task template'));
    } finally {
      setSubmitting(false);
    }
  };

  const resetForm = () => {
    setFormData({
      taskName: '',
      description: '',
      requiresApproval: false,
      approverEmail: '',
      defaultPicId: '',
      defaultTeamId: undefined,
      templateCategoryId: undefined
    });
    setSelectedTask(null);
  };

  const openAddModal = () => {
    resetForm();
    setShowAddModal(true);
  };

  return (
    <div className="px-4 sm:px-6 lg:px-8 py-8">
      <div className="sm:flex sm:items-center sm:justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900 dark:text-white">Task Template Bank</h1>
          <p className="mt-2 text-sm text-gray-700 dark:text-gray-300">
            Manage reusable task templates that can be used across multiple templates
          </p>
        </div>
        <div className="mt-4 sm:mt-0">
          <button
            onClick={openAddModal}
            className="inline-flex items-center px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-indigo-600 hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500"
          >
            <PlusIcon className="-ml-1 mr-2 h-5 w-5" />
            Add Task Template
          </button>
        </div>
      </div>

      {/* Search */}
      <div className="mt-6">
        <Input
          type="text"
          value={globalFilter ?? ''}
          onChange={e => setGlobalFilter(e.target.value)}
          placeholder="Search task templates..."
        />
      </div>

      {/* Table */}
      <div className="mt-6 flex flex-col">
        <div className="-my-2 -mx-4 overflow-x-auto sm:-mx-6 lg:-mx-8">
          <div className="inline-block min-w-full py-2 align-middle md:px-6 lg:px-8">
            <div className="overflow-hidden shadow ring-1 ring-black ring-opacity-5 md:rounded-lg">
              <table className="min-w-full divide-y divide-gray-300 dark:divide-gray-600">
                <thead className="bg-gray-50 dark:bg-gray-700">
                  {table.getHeaderGroups().map(headerGroup => (
                    <tr key={headerGroup.id}>
                      {headerGroup.headers.map(header => (
                        <th
                          key={header.id}
                          className="px-3 py-3.5 text-left text-sm font-semibold text-gray-900 dark:text-white cursor-pointer hover:bg-gray-100 dark:hover:bg-gray-600"
                          onClick={header.column.getToggleSortingHandler()}
                        >
                          <div className="flex items-center space-x-1">
                            {flexRender(header.column.columnDef.header, header.getContext())}
                            {{
                              asc: ' 🔼',
                              desc: ' 🔽',
                            }[header.column.getIsSorted() as string] ?? null}
                          </div>
                        </th>
                      ))}
                    </tr>
                  ))}
                </thead>
                <tbody className="divide-y divide-gray-200 dark:divide-gray-700 bg-white dark:bg-gray-800">
                  {loading ? (
                    <tr>
                      <td colSpan={columns.length} className="px-3 py-4 text-center text-sm text-gray-500 dark:text-gray-400">
                        Loading...
                      </td>
                    </tr>
                  ) : table.getRowModel().rows.length === 0 ? (
                    <tr>
                      <td colSpan={columns.length} className="px-3 py-4 text-center text-sm text-gray-500 dark:text-gray-400">
                        No task templates found
                      </td>
                    </tr>
                  ) : (
                    table.getRowModel().rows.map(row => (
                      <tr key={row.id} className="hover:bg-gray-50 dark:hover:bg-gray-700">
                        {row.getVisibleCells().map(cell => (
                          <td key={cell.id} className="whitespace-nowrap px-3 py-4 text-sm">
                            {flexRender(cell.column.columnDef.cell, cell.getContext())}
                          </td>
                        ))}
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>

            {/* Pagination */}
            <div className="flex items-center justify-between border-t border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 px-4 py-3 sm:px-6">
              <div className="flex flex-1 justify-between sm:hidden">
                <button
                  onClick={() => table.previousPage()}
                  disabled={!table.getCanPreviousPage()}
                  className="relative inline-flex items-center rounded-md border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 px-4 py-2 text-sm font-medium text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-600 disabled:opacity-50"
                >
                  Previous
                </button>
                <button
                  onClick={() => table.nextPage()}
                  disabled={!table.getCanNextPage()}
                  className="relative ml-3 inline-flex items-center rounded-md border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 px-4 py-2 text-sm font-medium text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-600 disabled:opacity-50"
                >
                  Next
                </button>
              </div>
              <div className="hidden sm:flex sm:flex-1 sm:items-center sm:justify-between">
                <div>
                  <p className="text-sm text-gray-700 dark:text-gray-300">
                    Showing{' '}
                    <span className="font-medium">
                      {table.getState().pagination.pageIndex * table.getState().pagination.pageSize + 1}
                    </span>{' '}
                    to{' '}
                    <span className="font-medium">
                      {Math.min(
                        (table.getState().pagination.pageIndex + 1) * table.getState().pagination.pageSize,
                        tasks.length
                      )}
                    </span>{' '}
                    of <span className="font-medium">{tasks.length}</span> results
                  </p>
                </div>
                <div className="flex space-x-2">
                  <button
                    onClick={() => table.setPageIndex(0)}
                    disabled={!table.getCanPreviousPage()}
                    className="relative inline-flex items-center rounded-md border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 px-2 py-2 text-sm font-medium text-gray-500 dark:text-gray-400 hover:bg-gray-50 dark:hover:bg-gray-600 disabled:opacity-50"
                  >
                    First
                  </button>
                  <button
                    onClick={() => table.previousPage()}
                    disabled={!table.getCanPreviousPage()}
                    className="relative inline-flex items-center rounded-md border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 px-2 py-2 text-sm font-medium text-gray-500 dark:text-gray-400 hover:bg-gray-50 dark:hover:bg-gray-600 disabled:opacity-50"
                  >
                    Previous
                  </button>
                  <button
                    onClick={() => table.nextPage()}
                    disabled={!table.getCanNextPage()}
                    className="relative inline-flex items-center rounded-md border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 px-2 py-2 text-sm font-medium text-gray-500 dark:text-gray-400 hover:bg-gray-50 dark:hover:bg-gray-600 disabled:opacity-50"
                  >
                    Next
                  </button>
                  <button
                    onClick={() => table.setPageIndex(table.getPageCount() - 1)}
                    disabled={!table.getCanNextPage()}
                    className="relative inline-flex items-center rounded-md border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 px-2 py-2 text-sm font-medium text-gray-500 dark:text-gray-400 hover:bg-gray-50 dark:hover:bg-gray-600 disabled:opacity-50"
                  >
                    Last
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Add/Edit Modal */}
      <Modal
        show={showAddModal || showEditModal}
        onClose={() => {
          setShowAddModal(false);
          setShowEditModal(false);
          resetForm();
        }}
        title={showEditModal ? 'Edit Task Template' : 'Add Task Template'}
      >
        <form onSubmit={handleSubmit} className="space-y-4">
          <div className="space-y-2">
            <Label htmlFor="taskName">
              Task Name <span className="text-red-500">*</span>
            </Label>
            <Input
              id="taskName"
              type="text"
              value={formData.taskName}
              onChange={e => setFormData({ ...formData, taskName: e.target.value })}
              placeholder="Enter task name"
              required
            />
          </div>

          <div className="space-y-2">
            <Label htmlFor="description">Description</Label>
            <textarea
              id="description"
              value={formData.description}
              onChange={e => setFormData({ ...formData, description: e.target.value })}
              rows={3}
              placeholder="Enter task description (optional)"
              className="flex w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50 transition-shadow duration-200"
            />
          </div>

          <div className="space-y-2">
            <Label htmlFor="category">Category</Label>
            <Select
              value={formData.templateCategoryId?.toString()}
              onValueChange={(value) => setFormData({ ...formData, templateCategoryId: value ? parseInt(value) : undefined })}
            >
              <SelectTrigger className="mt-1">
                <SelectValue placeholder="Select category (optional)" />
              </SelectTrigger>
              <SelectContent>
                {categories.map(cat => (
                  <SelectItem key={cat.id} value={cat.id?.toString() || 'cat-' + cat.id}>
                    {cat.name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <div className="flex items-center">
            <input
              type="checkbox"
              checked={formData.requiresApproval}
              onChange={e => setFormData({ ...formData, requiresApproval: e.target.checked })}
              className="h-4 w-4 text-indigo-600 focus:ring-indigo-500 border-gray-300 rounded"
            />
            <label className="ml-2 block text-sm text-gray-900 dark:text-white">
              Requires Approval
            </label>
          </div>

          {formData.requiresApproval && (
            <div className="space-y-2">
              <Label htmlFor="approverEmail">
                Approver Email <span className="text-red-500">*</span>
              </Label>
              <Input
                id="approverEmail"
                type="email"
                value={formData.approverEmail}
                onChange={e => setFormData({ ...formData, approverEmail: e.target.value })}
                placeholder="Enter approver email"
                required={formData.requiresApproval}
              />
            </div>
          )}

          <div className="space-y-2">
            <Label htmlFor="assignee">Default Assignee</Label>
            <select
              id="assignee"
              value={
                formData.defaultTeamId ? `team-${formData.defaultTeamId}` :
                formData.defaultPicId ? `person-${formData.defaultPicId}` :
                ''
              }
              onChange={(e) => {
                const value = e.target.value;
                if (value.startsWith('team-')) {
                  // Team assignment
                  const teamId = parseInt(value.replace('team-', ''));
                  setFormData({ ...formData, defaultTeamId: teamId, defaultPicId: '' });
                } else if (value.startsWith('person-')) {
                  // Person assignment
                  const picId = value.replace('person-', '');
                  setFormData({ ...formData, defaultPicId: picId, defaultTeamId: undefined });
                } else {
                  // Clear both
                  setFormData({ ...formData, defaultPicId: '', defaultTeamId: undefined });
                }
              }}
              className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50 transition-shadow duration-200"
            >
              <option value="">Select assignment (optional)...</option>
              <optgroup label="─── Teams ───">
                {teams.map((team) => (
                  <option key={`team-${team.id}`} value={`team-${team.id}`}>
                    👥 {team.name}
                  </option>
                ))}
              </optgroup>
              <optgroup label="─── Persons ───">
                {users.map((user) => (
                  <option key={`person-${user.id}`} value={`person-${user.id}`}>
                    👤 {user.userName} ({user.email})
                  </option>
                ))}
              </optgroup>
            </select>
          </div>

          <div className="flex justify-end space-x-3 pt-4">
            <button
              type="button"
              onClick={() => {
                setShowAddModal(false);
                setShowEditModal(false);
                resetForm();
              }}
              className="px-4 py-2 text-sm font-medium text-gray-700 dark:text-gray-300 bg-white dark:bg-gray-700 border border-gray-300 dark:border-gray-600 rounded-md hover:bg-gray-50 dark:hover:bg-gray-600"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={submitting}
              className="px-4 py-2 text-sm font-medium text-white bg-indigo-600 border border-transparent rounded-md hover:bg-indigo-700 disabled:opacity-50"
            >
              {submitting ? 'Saving...' : showEditModal ? 'Update' : 'Create'}
            </button>
          </div>
        </form>
      </Modal>

      {/* Delete Confirmation Dialog */}
      <Modal
        show={showDeleteDialog}
        onClose={() => {
          setShowDeleteDialog(false);
          setSelectedTask(null);
        }}
        title="Delete Task Template"
      >
        <div className="space-y-4">
          <p className="text-sm text-gray-500 dark:text-gray-400">
            Are you sure you want to delete the task template "<strong>{selectedTask?.taskName}</strong>"?
            This action cannot be undone.
          </p>
          <p className="text-sm text-red-600">
            Note: You cannot delete a task that is currently being used in any templates.
          </p>
          <div className="flex justify-end space-x-3">
            <button
              type="button"
              onClick={() => {
                setShowDeleteDialog(false);
                setSelectedTask(null);
              }}
              className="px-4 py-2 text-sm font-medium text-gray-700 dark:text-gray-300 bg-white dark:bg-gray-700 border border-gray-300 dark:border-gray-600 rounded-md hover:bg-gray-50 dark:hover:bg-gray-600"
            >
              Cancel
            </button>
            <button
              type="button"
              onClick={handleDelete}
              disabled={submitting}
              className="px-4 py-2 text-sm font-medium text-white bg-red-600 border border-transparent rounded-md hover:bg-red-700 disabled:opacity-50"
            >
              {submitting ? 'Deleting...' : 'Delete'}
            </button>
          </div>
        </div>
      </Modal>
    </div>
  );
}
